import { useCallback, useEffect, useMemo } from 'react';
import useSWRInfinite from 'swr/infinite';
import type { ReconciliationCursorPage } from '@shared/api/contracts';
import { RECONCILIATION_LIST_PAGE_SIZE } from '../lib/reconciliation-query';

const SWR_OPTIONS = {
  revalidateFirstPage: false,
} as const;

export type ReconciliationListPagination = {
  page: number;
  limit: number;
  total: number;
};

/** Cursor API + UI page numbers (как в других разделах ЛК, без total с бека). */
export function useReconciliationCursorList<T>(
  enabled: boolean,
  cacheKeyPrefix: string,
  querySignature: string,
  page: number,
  limit: number,
  fetchPage: (cursor?: string) => Promise<ReconciliationCursorPage<T>>,
) {
  const safePage = Math.max(1, page);

  const result = useSWRInfinite<ReconciliationCursorPage<T>>(
    (pageIndex, previous) => {
      if (!enabled) return null;
      if (previous && !previous.nextCursor) return null;
      const cursor = pageIndex === 0 ? '' : (previous?.nextCursor ?? '');
      if (pageIndex > 0 && !cursor) return null;
      return [cacheKeyPrefix, querySignature, cursor] as const;
    },
    ([, , cursor]) => fetchPage(cursor ? String(cursor) : undefined),
    SWR_OPTIONS,
  );

  const { setSize, mutate, size } = result;

  useEffect(() => {
    void setSize(1);
  }, [querySignature, setSize]);

  useEffect(() => {
    if (!enabled) return;
    if (size < safePage) {
      void setSize(safePage);
    }
  }, [enabled, safePage, setSize, size]);

  const pageIndex = safePage - 1;
  const currentSlice = result.data?.[pageIndex];
  const items = currentSlice?.data ?? [];
  const hasNext = Boolean(currentSlice?.nextCursor);

  const pagination: ReconciliationListPagination = useMemo(
    () => ({
      page: safePage,
      limit,
      total: (safePage - 1) * limit + items.length + (hasNext ? limit : 0),
    }),
    [hasNext, items.length, limit, safePage],
  );

  const isLoading =
    enabled &&
    !currentSlice &&
    !result.error &&
    (pageIndex === 0 ? !result.data : size < safePage);

  const refresh = useCallback(async () => {
    await setSize(1);
    await mutate();
  }, [mutate, setSize]);

  return {
    items,
    pagination,
    isLoading,
    isValidating: result.isValidating,
    error: enabled ? result.error : undefined,
    refresh,
  };
}

export { RECONCILIATION_LIST_PAGE_SIZE };
